import React from 'react'
import { useNavigate } from 'react-router'
import { post } from '@mobi/account/Utils'
import type { MfaUpdateAuthRequestData } from '@mobi/api-types'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Button } from '@mobi/component-library/Base/Buttons'
import { FeatureIcon, Icon } from '@mobi/component-library/Assets/Icon'
import { colors } from '@mobi/component-library/Foundations'
import { Content, Footer, SignUpPageStyled } from './SignUpPage.styled'
import { isReactNativeApp, isReactNativeIos, sendToNative } from '@mobi/web-native-comms/web'
import { useBiometricsStatus } from '../../Verification/Biometrics/Hooks/useBiometricsStatus'
import { Toggle } from '@mobi/component-library/Selection&Input/Toggle'
import {
  Selector,
  SelectorHeading,
  MessageContainer,
  ToggleGroupItem,
  ChildrenContainer,
} from './SecuritySettings.styled'
import { routes, trackSignUpAccountProtection, trackSignUpAccountProtectionSkipped } from '../Utils'
import { useSelector } from 'react-redux'
import { getSignUpState } from '../Store'

type Props = {
  betAccountNumber?: number
  password: string
  includeMfa: boolean
}

export const SecuritySettings = ({ betAccountNumber, password, includeMfa }: Props) => {
  const [isBiometricsToggleOn, setIsBiometricsToggleOn] = React.useState(true)
  const [isMfaToggleOn, setIsMfaToggleOn] = React.useState(true)
  const signUpState = useSelector(getSignUpState)

  const navigate = useNavigate()
  const { biometricsStatus } = useBiometricsStatus()

  const biometricsType = biometricsStatus?.biometricsType
  const isFaceId = biometricsType == 'FACIAL_RECOGNITION' && isReactNativeIos
  const biometricsPromptText = isFaceId ? 'Face ID' : 'PIN / Biometrics'
  const isBiometricsAvailable = isReactNativeApp && betAccountNumber && password !== ''
  const isMfaAvailable = includeMfa && signUpState?.mobileNumberVerified

  React.useEffect(() => {
    // clear any biometrics password that might be saved from a previously logged in user
    if (betAccountNumber) {
      sendToNative('REMOVE_BIOMETRICS_PASSWORD')
    }
  }, [betAccountNumber])

  React.useEffect(() => {
    if (!isBiometricsAvailable && !isMfaAvailable) {
      navigate(routes.depositLimit)
    }
  }, [isBiometricsAvailable, isMfaAvailable, navigate])

  if (!isBiometricsAvailable && !isMfaAvailable) {
    return null
  }

  return (
    <SignUpPageStyled>
      <AccountHeader
        alignItems='start'
        title='Protect your TABtouch account'
        description='Adding extra security will ensure you are the only one who can access your TABtouch account.'
        style={{ marginBottom: '0rem' }}
      />
      <Content>
        <Selector>
          <SelectorHeading>
            <Icon name='SolidLock01' color={colors.studio[500]} size='2.4rem' />
            <MessageContainer>
              <div>Security</div>
              <div>Make your account more secure</div>
            </MessageContainer>
          </SelectorHeading>
          {isBiometricsAvailable && (
            <ToggleGroupItem id='biometrics'>
              <FeatureIcon type='custom' size='md'>
                <Icon name='LineFaceId' color={colors.lavender['600']} size='1.6rem' />
              </FeatureIcon>
              <ChildrenContainer>{biometricsPromptText}</ChildrenContainer>
              <Toggle
                id='biometrics-toggle'
                isOn={isBiometricsToggleOn}
                onToggle={function (): void {
                  setIsBiometricsToggleOn(!isBiometricsToggleOn)
                }}
              />
            </ToggleGroupItem>
          )}
          {isMfaAvailable && (
            <ToggleGroupItem id='mfa'>
              <FeatureIcon type='custom' size='md'>
                <Icon name='LineShield01' color={colors.lavender['600']} size='1.6rem' />
              </FeatureIcon>
              <ChildrenContainer>Multi-Factor Authentication (MFA)</ChildrenContainer>
              <Toggle
                id='mfa-toggle'
                isOn={isMfaToggleOn}
                onToggle={function (): void {
                  setIsMfaToggleOn(!isMfaToggleOn)
                }}
              />
            </ToggleGroupItem>
          )}
        </Selector>
      </Content>
      <Footer>
        <Button
          color='primary'
          isFullWidth
          size='md'
          onClick={async () => {
            if (isBiometricsAvailable && isBiometricsToggleOn) {
              sendToNative('REQUEST_TOGGLE_BIOMETRICS', {
                accountNumber: betAccountNumber,
                password: password,
                doNotReauthenticate: true,
              })
            }

            if (isMfaAvailable && isMfaToggleOn) {
              // Enable MFA by setting SMS as the preferred factor. The customer's
              // mobile number has already been verified earlier in the sign-up flow.
              post<MfaUpdateAuthRequestData>('/api/mfa/update-auth-factor', {
                preferredFactor: 'SMS',
              })
            }

            trackSignUpAccountProtection(
              isMfaAvailable ? (isMfaToggleOn ? 'Enabled' : 'Disabled') : undefined,
              isBiometricsAvailable ? (isBiometricsToggleOn ? 'Enabled' : 'Disabled') : undefined
            )

            navigate(routes.depositLimit)
          }}
        >
          Continue
        </Button>
        <Button
          color='tertiary_color'
          isFullWidth
          size='md'
          onClick={() => {
            trackSignUpAccountProtectionSkipped()
            navigate(routes.depositLimit)
          }}
        >
          Skip for now
        </Button>
      </Footer>
    </SignUpPageStyled>
  )
}
